import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";
import { Guid } from "guid-typescript";

import Message from "../../types/system/Message";
import MessageTemplate from "../../types/system/MessageTemplate";
import MessageDetail, {
    MessageDetailMessage,
} from "../../types/system/MessageDetail";
import MessagingContact from "../../types/core/MessagingContact";
import Datastore from "../../communication/Datastore";
import DatastoreMessaging from "../../communication/DatastoreMessaging";
import store, { IAppState } from "../../state/Store";
import { getDisplayMode, setMessagingOpen } from "./UIConfiguration";
import {
    ConnectThread,
    DisconnectThread,
} from "../../sockets/messagingManager";
import AppState from "../../foundation/AppState";

// Define the type for our state store entry
type MessagingState = {
    contacts: MessagingContact[];
    recipients: MessagingContact[];
    templates: MessageTemplate[];
    unread: MessageDetail[];
    summary: MessageDetail[];
    openedMessage?: MessageDetail;
    thread: MessageDetailMessage[];
    threadIds: number[];
    threadIndex: number;
    threadMore: boolean;
    unreadStatus: string;
    summaryStatus: string;
    threadStatus: string;
    initStatus: string;
    templateStatus: string;
    contactStatus: string;
    templateAge?: Date;
    contactAge?: Date;
    unreadAge?: Date;
    error: string;
    newUnread: boolean;
};

// Define the initial state for our state store entry
const initialState: MessagingState = {
    contacts: [],
    recipients: [],
    templates: [],
    unread: [],
    summary: [],
    thread: [],
    threadIds: [],
    threadIndex: 0,
    threadMore: false,
    initStatus: "idle",
    templateStatus: "idle",
    contactStatus: "idle",
    unreadStatus: "idle",
    summaryStatus: "idle",
    threadStatus: "idle",
    error: null,
    newUnread: false,
};

// Create a slice to hold all our reducers
const messagingSlice = createSlice({
    name: "messaging",
    initialState: initialState,
    reducers: {
        setThread(state, action: PayloadAction<number[]>) {
            const userIds = action.payload.distinct().sort();
            const message = state.summary.find(
                item => item.Users.sort().join(",") === userIds.join(","),
            );

            if (message) state.openedMessage = message;

            state.thread = [];
            state.threadIds = userIds;
            state.threadIndex = 0;
            state.threadMore = true;
            DisconnectThread();
        },
        setRecipients(state, action: PayloadAction<number[]>) {
            state.recipients = state.contacts.filter(contact =>
                action.payload.includes(contact.UserID),
            );
        },
        addRecipient(state, action: PayloadAction<MessagingContact>) {
            if (
                state.recipients.some(
                    item => item.UserID === action.payload.UserID,
                )
            )
                return;

            state.recipients.push(action.payload);
            state.threadIds.push(action.payload.UserID);
        },
        removeRecipient(state, action: PayloadAction<MessagingContact>) {
            state.recipients = state.recipients.filter(
                item => item.UserID !== action.payload.UserID,
            );
            state.threadIds = state.threadIds.filter(
                id => id !== action.payload.UserID,
            );
        },
        insertThread(
            state,
            action: PayloadAction<[MessageDetailMessage, number]>,
        ) {
            action.payload[0].Message = parseMessage(
                action.payload[0],
                "light",
                action.payload[1],
            );
            state.thread.splice(0, 0, action.payload[0]);
        },
        insertSummary(state, action: PayloadAction<MessageDetailMessage>) {
            let current = state.summary.find(item =>
                item.Users.every(user => action.payload.Users.includes(user)),
            );
            if (!current) {
                current = {
                    Users: action.payload.Users,
                    Message: action.payload,
                    CreatedOn: action.payload.Sent.toDate(),
                };
                state.summary.splice(0, 0, current);
            } else {
                current.Message = action.payload;
                current.CreatedOn = action.payload.Sent.toDate();
            }
            parseSummary([current], state.contacts);
            state.summary.sort((a, b) => b.CreatedOn.compareDate(a.CreatedOn));
        },
        deleteThread(state, action: PayloadAction<MessageDetailMessage>) {
            state.thread = state.thread.filter(
                item => item.Id !== action.payload.Id,
            );
        },
        deleteSummary(state, action: PayloadAction<MessageDetailMessage>) {
            const current = state.summary.find(
                item => item.Message.Id === action.payload.Id,
            );
            if (!current) return;

            if (state.thread.length === 0) {
                state.summary = state.summary.filter(item => item !== current);
                return;
            }

            const thread = state.thread.last();
            current.Message = thread;
            current.CreatedOn = thread.Sent.toDate();
            parseSummary([current], state.contacts);
            state.summary.sort((a, b) => b.CreatedOn.compareDate(a.CreatedOn));
        },
        setThreadMessageLink(
            state,
            action: PayloadAction<{
                id: string;
                rect: DOMRect;
            }>,
        ) {
            const message = state.thread.find(
                item => item.Id === action.payload.id,
            );
            if (message) {
                message.LinkTop = `${action.payload.rect.top}px`;
                message.LinkLeft = `${action.payload.rect.left}px`;
                message.LinkWidth = `${action.payload.rect.width}px`;
                message.LinkHeight = `${action.payload.rect.height}px`;
            }
        },
        clearThread(state) {
            state.openedMessage = null;
            state.thread = [];
            state.recipients = [];
            state.threadIds = [];
            state.threadIndex = 0;
            state.threadMore = false;
            DisconnectThread();
        },
        clearNewUnread(state) {
            state.newUnread = false;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    contacts: rawData?.payload?.messaging?.contacts ?? [],
                    templates: rawData?.payload?.messaging?.templates ?? [],
                    unread: initialState.unread,
                    summary: initialState.summary,
                    thread: initialState.thread,
                    threadIds: initialState.threadIds,
                    recipients: initialState.recipients,
                    threadIndex: initialState.threadIndex,
                    threadMore: initialState.threadMore,
                    initStatus: initialState.initStatus,
                    templateStatus: initialState.templateStatus,
                    contactStatus: initialState.contactStatus,
                    unreadStatus: initialState.unreadStatus,
                    summaryStatus: initialState.summaryStatus,
                    threadStatus: initialState.threadStatus,
                    templateAge:
                        rawData?.payload?.messaging?.templateAge?.toDate(),
                    contactAge:
                        rawData?.payload?.messaging?.contactAge?.toDate(),
                    unreadAge: rawData?.payload?.messaging?.unreadAge?.toDate(),
                    error: initialState.error,
                    newUnread: initialState.newUnread,
                };
            })
            .addCase(initialiseMessaging.pending, (state: MessagingState) => {
                state.initStatus = "loading";
            })
            .addCase(initialiseMessaging.fulfilled, (state: MessagingState) => {
                state.initStatus = "succeeded";
            })
            .addCase(
                initialiseMessaging.rejected,
                (state: MessagingState, action: any) => {
                    state.initStatus = "failed";
                    state.error = action.error.message;
                },
            )
            .addCase(initialiseTemplates.pending, (state: MessagingState) => {
                state.templateStatus = "loading";
            })
            .addCase(
                initialiseTemplates.fulfilled,
                (
                    state: MessagingState,
                    action: PayloadAction<
                        [MessageTemplate[], MessageTemplate[]]
                    >,
                ) => {
                    if (!action.payload) return;

                    state.templateStatus = "succeeded";
                    state.templateAge = new Date();
                    state.templates = action.payload[0].concat(
                        action.payload[1],
                    );
                },
            )
            .addCase(
                initialiseTemplates.rejected,
                (state: MessagingState, action: any) => {
                    state.templateStatus = "failed";
                    state.error = action.error.message;
                },
            )
            .addCase(initialiseContacts.pending, (state: MessagingState) => {
                state.contactStatus = "loading";
            })
            .addCase(
                initialiseContacts.fulfilled,
                (
                    state: MessagingState,
                    action: PayloadAction<MessagingContact[]>,
                ) => {
                    if (!action.payload) return;

                    state.contactStatus = "succeeded";
                    state.contactAge = new Date();
                    state.contacts = action.payload;
                    for (const contact of state.contacts)
                        contact.Display = `${contact.FirstName} ${contact.LastName}`;
                },
            )
            .addCase(
                initialiseContacts.rejected,
                (state: MessagingState, action: any) => {
                    state.contactStatus = "failed";
                    state.error = action.error.message;
                },
            )
            .addCase(initialiseUnread.pending, (state: MessagingState) => {
                state.unreadStatus = "loading";
            })
            .addCase(
                initialiseUnread.fulfilled,
                (
                    state: MessagingState,
                    action: PayloadAction<MessageDetail[]>,
                ) => {
                    if (!action.payload) return;

                    state.unreadStatus = "succeeded";
                    state.unreadAge = new Date();
                    state.newUnread = action.payload.length > 0;
                    parseSummary(action.payload, state.contacts);
                    state.unread = action.payload;
                },
            )
            .addCase(
                initialiseUnread.rejected,
                (state: MessagingState, action: any) => {
                    state.unreadStatus = "failed";
                    state.error = action.error.message;
                },
            )
            .addCase(fetchMessagesUnread.pending, (state: MessagingState) => {
                state.unreadStatus = "loading";
            })
            .addCase(
                fetchMessagesUnread.fulfilled,
                (
                    state: MessagingState,
                    action: PayloadAction<MessageDetail[]>,
                ) => {
                    state.unreadStatus = "succeeded";
                    state.newUnread = action.payload.length > 0;
                    parseSummary(action.payload, state.contacts);
                    state.unread = action.payload;
                },
            )
            .addCase(
                fetchMessagesUnread.rejected,
                (state: MessagingState, action: any) => {
                    state.unreadStatus = "failed";
                    state.error = action.error.message;
                },
            )
            .addCase(fetchMessagingSummary.pending, (state: MessagingState) => {
                state.summaryStatus = "loading";
            })
            .addCase(
                fetchMessagingSummary.fulfilled,
                (
                    state: MessagingState,
                    action: PayloadAction<MessageDetail[]>,
                ) => {
                    state.summaryStatus = "succeeded";
                    parseSummary(action.payload, state.contacts);
                    state.summary = action.payload;
                    state.summary.sort((a, b) =>
                        b.CreatedOn.compareDate(a.CreatedOn),
                    );
                },
            )
            .addCase(
                fetchMessagingSummary.rejected,
                (state: MessagingState, action: any) => {
                    state.summaryStatus = "failed";
                    state.error = action.error.message;
                },
            )
            .addCase(fetchMessagesThread.pending, (state: MessagingState) => {
                state.threadStatus = "loading";
            })
            .addCase(
                fetchMessagesThread.fulfilled,
                (
                    state: MessagingState,
                    action: PayloadAction<{
                        result: MessageDetailMessage[];
                        displayMode: string;
                        userId: number;
                    }>,
                ) => {
                    state.threadStatus = "succeeded";
                    const users = state.threadIds
                        .concat([action.payload.userId])
                        .distinct()
                        .sort();
                    for (const message of action.payload.result) {
                        message.Message = parseMessage(
                            message,
                            action.payload.displayMode,
                            action.payload.userId,
                        );
                        message.Users = users;
                    }
                    state.thread = state.thread.concat(action.payload.result);
                    state.threadMore = action.payload.result.length > 0;
                    if (state.threadMore) state.threadIndex++;
                },
            )
            .addCase(
                fetchMessagesThread.rejected,
                (state: MessagingState, action: any) => {
                    state.threadStatus = "failed";
                    state.error = action.error.message;
                },
            );
    },
});

// eslint-disable-next-line no-control-regex
const extractEmoji = /[^\u0000-\u00ff]+/;
// eslint-disable-next-line no-control-regex
const removesEmoji = /[^\u0000-\u00ff]/gi;
const darkStyles = "<style>body,body>p{color:#fff!important}</style>";
const myFrameStyles =
    "<style>body,body>p{color:#fff!important}*{text-align:right}</style>";
const emojiStyle =
    "<style>html,body,body>p{font-size:30pt!important;line-height:30pt;padding:0;margin:0;}</style>";
const imageStyle =
    "<style>html,body,body>p{padding:0;margin:0;display: inline-block;}</style>";
const templateStyle =
    "<style>*{user-select:none;font-family:'Open Sans',Arial,Sans-Serif;line-height:18px;}.banner{display:none !important}table.body{width:538px !important;min-width:538px !important}div.body{padding:0 !important;border-bottom-left-radius:10px;border-bottom-right-radius:10px}.main{width:calc(100% - 40px) !important;min-width:auto !important;margin:20px 0 0 !important}.main.tble *{max-width:460px !important}.main.tble:last-child{display:none !important;}body>div.body,body>div.body>.layout{background:transparent}@font-face{font-family: 'Open Sans';font-style: normal;font-weight: 300;src:local('Open Sans Light'),local('OpenSans-Light'),url(fonts/mem5YaGs126MiZpBA-UN_r8OX-hpKKSTj5PW.woff2) format('woff2');unicode-range: U+0460-052F, U+1C80-1C88, U+20B4, U+2DE0-2DFF, U+A640-A69F,U+FE2E-FE2F;}</style>";
const templateDarkStyle =
    "<style>*{user-select:none}.layout{background:#444 !important}div.body{background:#444 !important}.main.tble{background:#222 !important;border: 1px solid #111 !important;box-shadow: 0 1px 5px #222 !important}*,body{color:#f9f9f9}</style>";

function getFrameStyles() {
    const fontSize = document.body.offsetWidth > 1024 ? "10pt" : "16px";
    return `
        <style>
            html,body{display:inline-block;float:left;}
            html,body,body>p{font-weight:500;font-size:${fontSize};line-height:${fontSize};font-family:'Open Sans',Arial,Sans-Serif;margin:0!important;padding:0!important;max-width:100%}
            body>p{padding:2px 0 1px 0 !important}
            table{table-layout:fixed}
            .bg{background:none!important}
            .es{margin:0!important;width:100%!important}
            .eo{display:none}
            img{max-width:100%!important}
        </style>`;
}

function getEmoji(value: string) {
    return (extractEmoji.exec(value || "") || [])[0] || "";
}

function removeEmoji(value: string) {
    return (value || "").replace(removesEmoji, "").replace("<p></p>", "");
}

function isEmoji(message: MessageDetailMessage) {
    return (message.Emojis?.length ?? 0) <= 10 && !message.Emojiless.length;
}

function isTemplated(message: MessageDetailMessage) {
    return message.Message.indexOf('class="content"') > 0;
}

function parseMessage(
    message: MessageDetailMessage,
    displayMode: string,
    userId: number,
) {
    message.Emojis = getEmoji(message.Message);
    message.Emojiless = removeEmoji(message.Message);

    let messageText = message.Message;

    if (messageText.indexOf('<p><img src="data:image/jpeg;base64') === 0) {
        messageText = `<html><head></head><body>${messageText}${imageStyle}</body></html>`;
    } else {
        messageText = messageText.replace(
            /cid:/g,
            "./api/Messaging/Attachment?messageId=" +
                encodeURIComponent(message.Id) +
                "&title=",
        );
        messageText = messageText.replace(
            /\[Year\]/g,
            new Date().getFullYear().toString(),
        );
        messageText = messageText.replace(/\[unsubscribeContent\]/g, "");

        let addStyles = "";
        if (isEmoji(message)) addStyles += emojiStyle;
        if (isTemplated(message)) {
            addStyles += templateStyle;
            if (displayMode === "dark") addStyles += templateDarkStyle;
        }

        messageText = `<html><head>${
            message.UserID === userId
                ? myFrameStyles
                : displayMode === "dark"
                ? darkStyles
                : ""
        }</head><body>${getFrameStyles()}${messageText}${addStyles}</body></html>`;
    }
    return messageText;
}

function parseSummary(messages: MessageDetail[], contacts: MessagingContact[]) {
    for (const message of messages) {
        message.CreatedOn = message.Message.Sent.toDate();

        if (message.Message.UserID === 1)
            message.SenderDetails = "Timetabling Solutions";
        else {
            message.UserDetails = message.Users.map(item =>
                contacts.find(contact => contact.UserID === item),
            ).filter(item => !!item);
            message.SenderDetails = message.UserDetails.map(
                item => item?.Display ?? "Unknown User",
            ).join(", ");
        }

        if (
            message.Message.Message.indexOf(
                '<p><img src="data:image/jpeg;base64',
            ) === 0
        )
            message.BareMessage =
                message.Message.UserID === this.user?.Id
                    ? "You sent an image"
                    : "Sent you an image";
        else message.BareMessage = message.Message.Message.fromHtml();
    }
}

export const {
    setThread,
    clearThread,
    clearNewUnread,
    setRecipients,
    addRecipient,
    removeRecipient,
    setThreadMessageLink,
} = messagingSlice.actions;

const { insertThread, insertSummary, deleteThread, deleteSummary } =
    messagingSlice.actions;

// This is a thunk, they are used to load the state
export const initialiseMessaging = createAsyncThunk(
    "messaging/initialiseMessaging",
    async (_, thunkAPI) => {
        await Promise.all([
            thunkAPI.dispatch(initialiseTemplates()),
            thunkAPI.dispatch(initialiseContacts()),
            thunkAPI.dispatch(initialiseUnread()),
        ]);
    },
    {
        condition: (_, { getState }) => {
            const { messaging } = (getState() as IAppState).db;
            return messaging.initStatus !== "loading";
        },
    },
);
export const initialiseTemplates = createAsyncThunk(
    "messaging/initialiseTemplates",
    async () => {
        const response = await Promise.all([
            Datastore.getType<MessageTemplate[]>("api/EmailTemplates"),
            Datastore.getType<MessageTemplate[]>(
                "api/EmailTemplates/System/GenericEmailTemplate",
            ),
        ]);
        return response;
    },
    {
        condition: (_, { getState }) => {
            const { messaging } = (getState() as IAppState).db;
            return (
                messaging.templateStatus !== "loading" &&
                (!messaging.templateAge ||
                    messaging.templateAge?.addDays(1) < new Date())
            );
        },
    },
);
export const initialiseContacts = createAsyncThunk(
    "messaging/initialiseContacts",
    async () => {
        return await DatastoreMessaging.getType<MessagingContact[]>(
            "api/Messaging/Contacts",
        );
    },
    {
        condition: (_, { getState }) => {
            const { messaging } = (getState() as IAppState).db;
            return (
                messaging.contactStatus !== "loading" &&
                (!messaging.contactAge ||
                    messaging.contactAge?.addMinutes(60 * 2) < new Date())
            );
        },
    },
);
export const initialiseUnread = createAsyncThunk(
    "messaging/initialiseUnread",
    async () => {
        return await DatastoreMessaging.getType<MessageDetail[]>(
            "api/Messaging/Unread",
        );
    },
    {
        condition: (_, { getState }) => {
            const { messaging } = (getState() as IAppState).db;
            return (
                messaging.unreadStatus !== "loading" &&
                (!messaging.unreadAge ||
                    messaging.unreadAge?.addSeconds(10) < new Date())
            );
        },
    },
);
export const fetchMessagingSummary = createAsyncThunk(
    "messaging/fetchMessagingSummary",
    async () => {
        return await DatastoreMessaging.getType<MessageDetail[]>(
            "api/Messaging/Summary",
        );
    },
    {
        condition: (_, { getState }) => {
            const { messaging } = (getState() as IAppState).db;
            return messaging.summaryStatus !== "loading";
        },
    },
);
export const fetchMessagesUnread = createAsyncThunk(
    "messaging/fetchMessagesUnread",
    async () => {
        return await DatastoreMessaging.getType<MessageDetail[]>(
            "api/Messaging/Unread",
        );
    },
);
export const openMessage = createAsyncThunk(
    "messaging/openMessage",
    async (message: MessageDetail, thunkAPI: any) => {
        thunkAPI.dispatch(setMessagingOpen(true));
        await thunkAPI.dispatch(fetchMessagingSummary());
        const userIds = message.Users.concat([message.Message.UserID]);
        thunkAPI.dispatch(setRecipients(userIds));
        thunkAPI.dispatch(openThread(userIds));
    },
);
export const openThread = createAsyncThunk(
    "messaging/openThread",
    async (users: number[], thunkAPI: any) => {
        const userId = Number(AppState.jwt.sub);
        thunkAPI.dispatch(setThread(users));
        thunkAPI.dispatch(fetchMessagesThread());
        ConnectThread(
            users.concat([userId]).distinct().sort().join(","),
            data => {
                thunkAPI.dispatch(insertThread([JSON.parse(data), userId]));
            },
        );
    },
    {
        condition: (_, { getState }) => {
            const { messaging } = (getState() as IAppState).db;
            return messaging.threadStatus !== "loading";
        },
    },
);
export const getPushKey = createAsyncThunk("messaging/getPushKey", async () => {
    return await DatastoreMessaging.get("api/Messaging/PushKey");
});
export const hasPush = createAsyncThunk(
    "messaging/hasPush",
    async (push: PushSubscription) => {
        return await DatastoreMessaging.postType<boolean>(
            "api/Messaging/HasPush",
            push,
        );
    },
);
export const setPush = createAsyncThunk(
    "messaging/setPush",
    async (push: PushSubscription) => {
        return await DatastoreMessaging.postType<boolean>(
            "api/Messaging/StorePush",
            push,
        );
    },
);
export const subscribe = createAsyncThunk(
    "messaging/subscribe",
    async (createNew: boolean, thunkAPI) => {
        const [registration, pushkey] = await Promise.all([
            navigator.serviceWorker.ready,
            thunkAPI.dispatch(getPushKey()),
        ]);

        let push = await registration.pushManager.getSubscription();

        if (!push && createNew)
            push = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: pushkey.payload,
            });

        if (!!push) await thunkAPI.dispatch(setPush(push));
    },
);
export const fetchMessagesThread = createAsyncThunk(
    "messaging/fetchMessagesThread",
    async () => {
        const { messaging } = store.getState().db;
        if (!messaging.threadMore)
            return {
                result: [] as MessageDetailMessage[],
                displayMode: "light",
                userId: Number(AppState.jwt.sub),
            };
        const result = await DatastoreMessaging.postType<
            MessageDetailMessage[]
        >("api/Messaging/Thread/" + messaging.threadIndex, messaging.threadIds);
        if (result.length > 0)
            await DatastoreMessaging.post(
                "api/Messaging/MarkRead",
                result.map(item => item.Id),
            );
        return {
            result,
            displayMode: getDisplayMode(),
            userId: Number(AppState.jwt.sub),
        };
    },
    {
        condition: (_, { getState }) => {
            const { messaging } = (getState() as IAppState).db;
            return messaging.threadStatus !== "loading";
        },
    },
);
export const sendMessage = createAsyncThunk(
    "messaging/sendMessage",
    async (payload: Message, thunkAPI) => {
        const { ui } = store.getState().db;
        const details = payload.toMessageDetailMessage();
        const userId = Number(AppState.jwt.sub);
        details.UserID = userId;
        if (ui.messagingOpen) {
            thunkAPI.dispatch(insertThread([details, userId]));
            thunkAPI.dispatch(insertSummary(details));
        }
        const response = await Promise.all([
            DatastoreMessaging.postType<Guid>("api/Messaging/Send", payload),
        ]);
        return response[0];
    },
);
export const deleteMessage = createAsyncThunk(
    "messaging/deleteMessage",
    async (payload: MessageDetailMessage, thunkAPI) => {
        thunkAPI.dispatch(deleteThread(payload));
        thunkAPI.dispatch(deleteSummary(payload));
        await Promise.all([
            DatastoreMessaging.post("api/Messaging/Delete", {
                Users: payload.Users,
                Message: {
                    Id: payload.Id,
                    UserID: payload.UserID,
                    Message: payload.Message,
                    Sent: payload.Sent,
                    Read: true,
                },
            }),
        ]);
    },
);
export const broadcastMessage = createAsyncThunk(
    "messaging/broadcastMessage",
    async (payload: [Message, number], thunkAPI) => {
        const [message, target] = payload;
        const response = await Promise.all([
            DatastoreMessaging.postType<Guid>(
                `api/Messaging/Broadcast/${target}`,
                message,
            ),
        ]);
        return response[0];
    },
);
export const sendActivationMessage = createAsyncThunk(
    "messaging/sendActivationMessage",
    async (userIds: number[]) => {
        await Promise.all([
            Datastore.postType<Guid>("api/Users/Activations", userIds),
        ]);
    },
);

// We have to export the reducer as this is added to the state store
export default messagingSlice.reducer;
